import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

const STORAGE_KEY = 'orcamento.ocultarValores';

type Ctx = {
  ocultar: boolean;
  alternar: () => void;
};

const OcultarValoresContext = createContext<Ctx | null>(null);

/**
 * Estado global "estou olhando essa tela com outra pessoa por perto" —
 * quando ligado, valores monetários renderizam como `R$ ●●●` em vez do
 * número real. Persiste em localStorage pra sobreviver entre janelas e
 * boots do app (é uma preferência do usuário, não estado de sessão).
 *
 * Usado por orçamento (kanban + visualização), financeiro e relatórios.
 */
export function OcultarValoresProvider({ children }: { children: ReactNode }) {
  const [ocultar, setOcultar] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, ocultar ? '1' : '0');
    } catch {
      // localStorage indisponível (modo privado/sandbox) — segue sem persistir.
    }
  }, [ocultar]);

  const alternar = useCallback(() => setOcultar((v) => !v), []);

  const valor = useMemo<Ctx>(() => ({ ocultar, alternar }), [ocultar, alternar]);

  return (
    <OcultarValoresContext.Provider value={valor}>
      {children}
    </OcultarValoresContext.Provider>
  );
}

export function useOcultarValores(): Ctx {
  const ctx = useContext(OcultarValoresContext);
  if (!ctx) {
    throw new Error('useOcultarValores: faltou <OcultarValoresProvider> na árvore.');
  }
  return ctx;
}

/**
 * Formata um número como moeda BRL, ou retorna `R$ ●●●●` quando o
 * usuário pediu pra ocultar. Use no map de tabelas/cards de orçamento,
 * financeiro e relatórios. Para campos não-monetários (quantidade,
 * estoque) NÃO aplicamos a máscara — só valores em dinheiro.
 */
const formatadorMoeda = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

export function formatarValorMonetario(valor: number, ocultar: boolean): string {
  if (ocultar) return 'R$ ●●●●';
  return formatadorMoeda.format(valor || 0);
}
